/**
 * client/src/ui/Block.tsx — a block declares WHAT KIND OF CONTENT it is. It never picks a pixel.
 *
 * WHAT WAS WRONG (owner, 2026-08-29: "the formatting is off and does not flow the way a website
 * should ... you have so much wasted landscape on the screen. its like part is developed for a
 * phone and other parts are half ass done ... as you run thr the pages the blocks and fields are
 * all way off").
 *
 * `measures.ts` already stated the right system one level up: a SCREEN declares a content kind and
 * `AppShell` applies it once. The layer below was never migrated. Inside a page, blocks carried
 * hand-written pixel numbers chosen one at a time, so a single 1200px `form` page rendered:
 *
 *     an 880 header, then a 1200 table, then a run of 640 panels — each left-aligned in a
 *     centred page, so the right edge stepped in three times and half the glass sat empty.
 *
 * Measured on the working tree the morning this was written: 34 numeric `maxWidth` literals outside
 * `ui/`, of which 24 were reading measures in 5 distinct values (560, 620, 640, 760, 880) — beside
 * 18 uses of the token `measure.copy`, which is also 640. The same measure, written two ways, in
 * one codebase, with nothing to say which one a new block should get.
 *
 * THE RULE THIS FILE EXISTS TO MAKE UNBREAKABLE. `measureLiterals.test.ts` scans the source and
 * fails on any `maxWidth` of 480px or more outside `ui/`. So there is no longer a way to hand-pick
 * a reading measure: you declare a kind, and the kind resolves to a number in exactly one place.
 *
 * WHAT IS DELIBERATELY *NOT* FORBIDDEN. A logo preview at 240, a street-view thumbnail at 340, a
 * table cell's text clamp at 150, one input at 360 — those are properties of the COMPONENT, not of
 * the page. Naming them in the measure map would make the map a junk drawer. They stay where they
 * are, and the test's 480px floor is what keeps the two populations apart.
 *
 * ALIGNMENT IS A DECISION, AND IT IS MADE HERE ONCE (D1, 2026-08-29). Before this file, exactly one
 * screen in the app centred its inner block (`BrandingScreen`, `margin: 0 auto`) and every other
 * one hung it on the left. Nobody decided that; each screen chose. `align` now defaults to `start`
 * — narrow blocks share the page's left edge with the wide ones, so the eye has one return path —
 * and a screen that wants the other behaviour has to say `align="center"` out loud.
 */
import type { CSSProperties, ElementType, ReactNode } from "react";

import { measure } from "./measures";

/**
 * The measures a block may ask for, by name.
 *
 *   copy   640   A paragraph or a column of fields INSIDE a wider page. The measure that stops a
 *                sentence stretching to 150 characters when the page widened for a grid.
 *   prose  880   A screen's opening block — heading, subject line, the instruction under it.
 *   form  1200   Cards, side-by-side panels, multi-column field rows.
 *   data  2044   A table whose width is the sum of its columns.
 *   page         No cap of its own: fill whatever the page gives you. For the block that IS the
 *                page's widest thing, so it never has to repeat the page's own number.
 */
export type BlockMeasure = "copy" | "prose" | "form" | "data" | "page";

/** `start` shares the page's left edge with the wide blocks. `center` centres inside the page. */
export type BlockAlign = "start" | "center";

const CAP: Record<Exclude<BlockMeasure, "page">, number> = {
  copy: measure.copy,
  prose: measure.prose,
  form: measure.form,
  data: measure.data,
};

export interface BlockProps {
  /** What kind of content this is. Defaults to `page` — no cap, fill the page. */
  measure?: BlockMeasure;
  /** Where it sits inside a wider page. Defaults to `start`. */
  align?: BlockAlign;
  /** The element to render. A block is a layout box, so it is a `div` unless told otherwise. */
  as?: ElementType;
  /** Anything the block needs that is not width — spacing, borders, background. */
  style?: CSSProperties;
  className?: string;
  id?: string;
  role?: string;
  children?: ReactNode;
}

/**
 * The width half of a block, resolved from its kind. Exported on its own because a component that
 * already renders its own element (a `<p>` with a role, a `<section>` with a border) should be able
 * to take the measure without being wrapped in another `div`.
 */
export function blockStyle(kind: BlockMeasure = "page", align: BlockAlign = "start"): CSSProperties {
  const width: CSSProperties = { width: "100%" };
  if (kind !== "page") width.maxWidth = CAP[kind];
  if (align === "center") width.marginInline = "auto";
  return width;
}

export function Block({
  measure: kind = "page",
  align = "start",
  as: Tag = "div",
  style,
  className,
  id,
  role,
  children,
}: BlockProps) {
  return (
    <Tag id={id} role={role} className={className} style={{ ...blockStyle(kind, align), ...style }}>
      {children}
    </Tag>
  );
}

/* --------------------------------------------------------------------------- *
 * THE PAIRED BAND (D1 option C, 2026-08-29)                                     *
 * --------------------------------------------------------------------------- */

/**
 * Two blocks side by side inside a wider page.
 *
 * WHY THIS EXISTS AND NOT JUST A WIDER PARAGRAPH. On step 2, three panels at 640 sat stacked in a
 * 1200 page, and 47% of the glass was empty to their right at EVERY desktop width — 600px at 1280,
 * 716 at 1512, 920 at 1920, 1240 at 2560. The wrong fix is to widen the panels: `form`'s 1200 cap
 * is derived (six review modules at `minmax(180px, 1fr)` plus gaps is 1170; the worksheet's field
 * rows are `22px 1.4fr 1fr 1.2fr`), and a two-column form stretched to 2000px is the defect that
 * reasoning exists to prevent. The right fix is a SECOND COLUMN OF CONTENT.
 *
 * On step 2 the content was already asking for it. The section says "Two boxes, on purpose. One is
 * for things you can put a number on ... The other is for everything else" — and then rendered them
 * one above the other. Side by side, the sentence is finally true of the screen.
 *
 * THE BAND UNSTACKS RATHER THAN CRUSHING, AND IT DOES SO ON ITS CONTAINER, NOT THE VIEWPORT.
 * `repeat(auto-fit, minmax(min(100%, minColumn), 1fr))` is the whole mechanism: while the container
 * can hold two columns of at least `minColumn`, it holds two; below that it holds one and the
 * children stack in source order. No media query, no viewport assumption, and no JavaScript — so a
 * band dropped into an 880 `prose` page stacks by itself rather than crushing, and the same band in
 * a 1200 `form` page pairs at every desktop width including 1280.
 *
 * WHY 440 AND NOT THE COPY MEASURE. The threshold is `2 * minColumn + gap`. At 440 that is 904, so
 * a band pairs inside a `form` page (1200 → two columns of 588) and stacks inside a `prose` page
 * (880 → one column) — which is the behaviour each page kind should have. A larger floor would
 * never pair at 1280; a smaller one would pair two 400px columns of prose, which reads worse than
 * stacking.
 *
 * IT ALSO HOLDS ONLY WHILE BOTH COLUMNS HAVE SOMETHING TO SAY. A band with one child renders that
 * child full width instead of leaving a column-shaped hole — the same rule the layout work of
 * 2026-08-28 settled for the CRM's two-column bands.
 */
export interface BandProps {
  /** The narrowest a column may get before the band unstacks. See the note on 440 above. */
  minColumn?: number;
  /** Space between the columns, in px. Defaults to 24 — the app's section gap. */
  gap?: number;
  style?: CSSProperties;
  children?: ReactNode;
}

/** The default column floor. See "WHY 440 AND NOT THE COPY MEASURE" above. */
export const BAND_MIN_COLUMN = 440;

/** The container width at which a band with this floor stops pairing. Exported so a test can state it. */
export function bandUnstacksBelow(minColumn = BAND_MIN_COLUMN, gap = 24): number {
  return minColumn * 2 + gap;
}

export function Band({ minColumn = BAND_MIN_COLUMN, gap = 24, style, children }: BandProps) {
  const paired = childCount(children) > 1;
  return (
    <div
      data-band={paired ? "paired" : "single"}
      style={{
        display: "grid",
        gap,
        // One child is not a band. Rendering the grid anyway leaves a column-shaped hole.
        gridTemplateColumns: paired
          ? `repeat(auto-fit, minmax(min(100%, ${minColumn}px), 1fr))`
          : "minmax(0, 1fr)",
        alignItems: "start",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Children that will actually render. `null`, `false` and `undefined` are not columns. */
function childCount(children: ReactNode): number {
  if (children == null || children === false) return 0;
  if (!Array.isArray(children)) return 1;
  return children.filter((c) => c != null && c !== false).length;
}
